import type { ApiError } from "../../api/result.ts";
import type { AnswerValue, DraftAnswer, FillView, Product, Question } from "../../api/domain.ts";
import { isInformational } from "../../api/domain.ts";
import type { Dictionary } from "../../i18n/dictionary.ts";
import type { Coverage } from "../../lib/format.ts";

/** Page state as a discriminated union; the view switches on it, no boolean flags. */
export type FillState =
  | { state: "loading" }
  | { state: "open"; view: FillView }
  | { state: "dead"; reason: DeadReason }
  | { state: "failed"; error: ApiError };

/**
 * Why a magic link shows no form. The server answers one status for unknown,
 * inactive and already evaluated links on purpose, so they share one state.
 */
export type DeadReason = "unavailable" | "submitted";

/** Autosave state per answer. */
export type SaveState =
  | { state: "idle" }
  | { state: "saving" }
  | { state: "saved" }
  | { state: "failed"; error: ApiError };

/**
 * A 401 on a token-scoped route means the link is unknown, inactive or already
 * evaluated; anything else is a real failure the reader can retry.
 */
export const deadOrFailed = (error: ApiError): FillState =>
  error.kind === "server" && error.envelope.kind === "unauthorized"
    ? { state: "dead", reason: "unavailable" }
    : { state: "failed", error };

/**
 * An answer carries its product, so a draft is identified by product and
 * question together.
 */
export const draftFor = (
  drafts: DraftAnswer[],
  product: string,
  question: string,
): DraftAnswer | undefined =>
  drafts.find((draft) => draft.product === product && draft.question === question);

export const upsertDraft = (drafts: DraftAnswer[], draft: DraftAnswer): DraftAnswer[] => {
  const index = drafts.findIndex(
    (candidate) => candidate.product === draft.product && candidate.question === draft.question,
  );
  if (index < 0) return [...drafts, draft];
  return drafts.map((candidate, position) => (position === index ? draft : candidate));
};

/** An empty value is "I don't know": it counts as unanswered and stays unscored. */
export const hasValue = (value: AnswerValue | null | undefined): boolean => {
  if (value === undefined || value === null) return false;
  if (value.kind === "text") return value.value.trim().length > 0;
  if (value.kind === "multi_select") return value.value.length > 0;
  return true;
};

export const isAnswered = (draft: DraftAnswer | undefined): boolean => hasValue(draft?.value);

/** Only scored questions drive progress; text questions are context only. */
export const scoredQuestions = (questions: Question[]): Question[] =>
  questions.filter((question) => !isInformational(question));

export const progressOf = (view: FillView, product: string): Coverage => {
  const scored = scoredQuestions(view.questions);

  return {
    answered: scored.filter((question) => isAnswered(draftFor(view.drafts, product, question.id)))
      .length,
    total: scored.length,
  };
};

export const selectedOptionSlugs = (value: AnswerValue | null | undefined): string[] => {
  if (value === undefined || value === null) return [];
  if (value.kind === "select") return [value.value];
  if (value.kind === "multi_select") return [...value.value];
  return [];
};

/**
 * The product a vendor answers for, which the questionnaire fixed when it was
 * created. The fill view carries that one product and no other, so there is
 * nothing to choose and nothing to learn about the rest of the field.
 */
export const productOf = (products: Product[]): string => products[0]?.id ?? "";

export type TerminalCopy = {
  title: string;
  body: string;
};

/** Friendly wording for every way a magic link can show no form. */
export const terminalCopy = (dictionary: Dictionary, reason: DeadReason): TerminalCopy =>
  reason === "submitted"
    ? { title: dictionary.dead.submittedTitle, body: dictionary.dead.submittedBody }
    : { title: dictionary.dead.unavailableTitle, body: dictionary.dead.unavailableBody };
